import React, { useRef } from 'react';
import { Icons } from '../ui/Icon';
import { useModalAccessibility } from '../../utils/use-modal-accessibility';

// ============================================================================
// ConfirmModal — v5, "quiet professional"
// ----------------------------------------------------------------------------
// Peter (2026-07-31), on the v4 dialog: "I dont like the shape, design,
// wording/copy, and icon of this warning modal. In fact, I don't like these
// things AT ALL. I want it to look more professional like something a company
// from Zendesk or Microsoft would do but in the QuoteMaker context. (not just
// orange please)."
//
// That is a full rejection, so this is a rewrite rather than a polish — and it
// is a rewrite BACK TOWARDS the house references, not away from them.
// design_preferences_peter.md names Linear, Stripe, Vercel, Notion and
// claude.ai, and explicitly rejects "retro-skeuomorphic" and
// "gradients-everywhere SaaS". v4 had drifted into exactly that: a 66px glossy
// moulded placard, multi-stop gradient keys with inset bevels and text-shadow,
// and buttons that physically depressed. Professional dialogs do none of that.
//
// WHAT CHANGED, and why each one
//  · LEFT-ALIGNED, not centred. A centred column under a big badge is the
//    consumer-alert cliché; every reference app aligns a dialog's text with its
//    content.
//  · The icon is a 30px tinted tile with an 16px glyph, not a 66px placard. It
//    identifies the KIND of decision and then gets out of the way.
//  · The eyebrow is gone as a shouty uppercase red bar. Its WCAG 1.4.1 job — the
//    tone must not be carried by colour alone — is now done by a plain
//    sentence-case consequence line under the title, which is also better copy.
//  · Buttons are RIGHT-ALIGNED on a footer bar, 36px, flat, one hairline border,
//    a 1px hover lift. Cancel stays FIRST in the DOM (a11y test pins this and
//    the visual hierarchy is deliberate) and reads left of the primary.
//  · Semantic STATUS colour, never brand. docs/design-system.md: "Status colours
//    are not brand ... never follow --qm-accent." So a destructive dialog is red
//    and a neutral one is blue — which is the literal answer to "not just
//    orange".
//  · Density: 34px body padding + 66px badge + 52px keys became 18/30/36. The
//    standing rule is that every pixel earns its keep.
//
// API UNCHANGED — all 9 direct render sites and the 64 showConfirm() callers
// keep working untouched. `confirmColor` still accepts red/orange/blue and
// `icon` still accepts warning/danger/info, because
// component-library/entries/ConfirmModalEntry.tsx publishes both lists.
// ============================================================================

const INK = '#1a1a1a';

/**
 * One tone = the tile, the consequence line and the primary key.
 * `text` is the AA-on-white stop (never the bright fill) so the consequence
 * line stays readable; `fill`/`edge` dress the solid primary key.
 * Kept as literals with the token as the source of truth in the comment:
 * these are inline styles, and a var() here would resolve to nothing in the
 * renderToStaticMarkup characterization tests.
 */
const TONES: Record<string, { fill: string; edge: string; hover: string; text: string; tint: string; tintEdge: string }> = {
    // --qm-status-error #ef4444
    red: { fill: '#dc2626', edge: '#b91c1c', hover: '#b91c1c', text: '#b91c1c', tint: '#fef2f2', tintEdge: '#f6cfcf' },
    // --qm-accent #EB7232 — caution, not danger
    orange: { fill: 'var(--qm-accent)', edge: 'oklch(from var(--qm-accent) 0.579 calc(c * 0.9273) calc(h - 1.9))', hover: 'oklch(from var(--qm-accent) 0.579 calc(c * 0.9273) calc(h - 1.9))', text: '#b45309', tint: '#fff5ee', tintEdge: '#f0d4bd' },
    // --qm-status-info #3b82f6
    blue: { fill: '#2563eb', edge: '#1d4ed8', hover: '#1d4ed8', text: '#1d4ed8', tint: '#eff6ff', tintEdge: '#c3d9f7' },
};

// `info` used to share the warning triangle, which said "something is wrong"
// about a dialog whose whole job is that nothing is. A hazard glyph on a
// neutral message is a small lie told 64 times.
const ICON_FOR: Record<string, any> = { warning: Icons.AlertTriangle, danger: Icons.Trash, info: Icons.Info };

/**
 * The consequence line — DANGER ONLY, deliberately.
 *
 * It exists because an irreversible action must say so in TEXT: WCAG 1.4.1, and
 * the characterization test asserts the "This can" substring. But the first cut
 * also gave `warning` ("Please confirm before continuing.") and `info` ("Worth
 * knowing before you continue.") a line each, and those said nothing the title
 * did not already say — filler on a surface whose standing rule is that every
 * pixel earns its keep. Warning and info carry their meaning in the title and
 * message, so their tone is never colour-alone either; the tile is decorative
 * and aria-hidden.
 */
const CONSEQUENCE_FOR: Record<string, string> = {
    danger: 'This can’t be undone.',
};

const KEY_BASE: React.CSSProperties = {
    height: '36px',
    padding: '0 15px',
    minHeight: 0,
    borderRadius: '7px',
    fontSize: '13.5px',
    fontWeight: 700,
    letterSpacing: '0.005em',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    transition: 'transform 0.1s ease, box-shadow 0.12s ease, background 0.12s ease, border-color 0.12s ease',
};

interface ConfirmModalProps {
    isOpen?: boolean;
    onClose: () => void;
    onConfirm: () => void;
    onCancel?: () => void;
    title?: any;
    message?: any;
    confirmText?: string;
    cancelText?: string;
    confirmColor?: string;
    icon?: string;
    zIndex?: number | string;
    /**
     * Which key receives focus when the dialog opens. Defaults to 'cancel',
     * which is the historical behaviour. The key carries the default-focus
     * mark itself, so a focusable part of the message (a scrollable text box)
     * never takes the opening focus. Pass 'confirm' when the cancel key
     * performs the forward action, so a reflexive Enter still lands on the
     * safe choice. Pass 'dialog' for a question that opens on its own while the
     * operator may be typing: focus goes to the card, so a stray Enter or Space
     * presses neither key.
     */
    defaultFocus?: 'cancel' | 'confirm' | 'dialog';
    /**
     * Optional small muted line under the message — for the escape route or a
     * consequence note ("Esc keeps you where you are"), so the message itself
     * stays about the decision.
     */
    hint?: React.ReactNode;
}

export default function ConfirmModal({ isOpen, onClose, onConfirm, onCancel, title, message, confirmText = 'Confirm', cancelText = 'Cancel', confirmColor = 'red', icon = 'warning', zIndex, defaultFocus = 'cancel', hint }: ConfirmModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);
    // UI-3 (register 2026-04-21): wire aria-labelledby to the confirm dialog title.
    // A confirmation outranks the surface that raised it for Escape, Tab and opening focus, even if that surface mounts again
    // while the question is open (see the keyboard-layer notes in use-modal-accessibility).
    const modalProps = useModalAccessibility(modalRef, onClose, isOpen, 'qm-confirm-modal-title', { decisionDialog: true });

    if (!isOpen) return null;

    const t = TONES[confirmColor] || TONES.red!;
    const TypeIcon = ICON_FOR[icon] || Icons.AlertTriangle;
    const consequence = CONSEQUENCE_FOR[icon];

    const effectiveZIndex = (typeof zIndex === 'number' || typeof zIndex === 'string') ? zIndex : 10200;

    // A 1px lift and a slightly deeper shadow. Peter's standing note is that a
    // hover must be tactile rather than a background swap, but v4's physical
    // depress was the part that read as "vibe coded" — this is the restrained
    // version of the same idea.
    const lift = (e: any) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 10px -3px rgba(26,26,26,0.28)'; };
    const drop = (e: any) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 2px rgba(26,26,26,0.10)'; };

    return (
        <div className="fixed inset-0 flex items-center justify-center"
            style={{
                background: 'rgba(26,26,26,0.45)',
                backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)',
                zIndex: effectiveZIndex, padding: '16px',
            }}
            onClick={onClose}>
            <div
                ref={modalRef}
                {...modalProps}
                data-qm-default-focus={defaultFocus === 'dialog' ? '' : undefined}
                className="w-full max-w-md"
                style={{
                    position: 'relative',
                    borderRadius: '12px',
                    background: '#ffffff',
                    // A hairline and real elevation, not a 2.5px outline. The
                    // card-industrial 2px ink border is the signature for
                    // PRIMARY SURFACES; a dialog floating over a dimmed page
                    // gets its separation from the shadow instead.
                    border: '1px solid #e4e1db',
                    boxShadow: '0 20px 44px -14px rgba(26,26,26,0.30), 0 6px 14px -6px rgba(26,26,26,0.16)',
                    overflow: 'hidden',
                    // The EXPLANATION scrolls; the keys never do. The bulk-delete
                    // dialog composes its message from a list of project titles,
                    // so a long one used to grow the card past the viewport and
                    // push the decision off the bottom of the screen — the one
                    // thing a dialog exists to present. Same rule the Run Sheet
                    // preview panel follows: reference scrolls, actions stay.
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'min(80vh, 640px)',
                    animation: 'qm-modal-pop 200ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ display: 'flex', gap: '13px', padding: '18px 20px 16px', minHeight: 0, overflowY: 'auto' }}>
                    {/* Identifies the KIND of decision, then gets out of the way.
                        aria-hidden because the consequence line carries the
                        meaning in text — colour and glyph never carry it alone. */}
                    <div
                        aria-hidden="true"
                        style={{
                            flex: '0 0 auto',
                            width: '30px', height: '30px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            borderRadius: '8px',
                            background: t.tint,
                            border: `1px solid ${t.tintEdge}`,
                            color: t.text,
                        }}
                    >
                        <TypeIcon size={16} strokeWidth={2.2} />
                    </div>

                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                        {/* overflow-wrap on both: a project title or a filename is
                            frequently one unbroken token
                            ("TBE_Royston_St-_Scope_of_works_TASK_ID_8844570.pdf"),
                            and without this it pushes the card wider than the
                            viewport instead of wrapping. */}
                        <h3 id="qm-confirm-modal-title" style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '-0.005em', color: INK, margin: '3px 0 0', lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                            {title}
                        </h3>
                        {consequence && (
                            <p style={{ fontSize: '12px', fontWeight: 700, color: t.text, margin: '4px 0 0', lineHeight: 1.4 }}>
                                {consequence}
                            </p>
                        )}
                        {/* pre-line so callers can separate a message into readable
                            paragraphs with \n. Single-line callers are unaffected;
                            the RFQ pre-flight dialog already joined its lines with
                            \n and was being collapsed into one blob. */}
                        <p style={{ fontSize: '13.5px', lineHeight: 1.55, color: '#4b5563', margin: consequence ? '9px 0 0' : '7px 0 0', whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                            {message}
                        </p>
                        {hint && (
                            <p style={{ fontSize: '12px', lineHeight: 1.5, color: '#6b7280', margin: '8px 0 0' }}>
                                {hint}
                            </p>
                        )}
                    </div>
                </div>

                {/* Footer bar. Right-aligned, because that is where a decision
                    lands in every reference app, and tinted so the action row
                    reads as a separate register from the explanation above it. */}
                <div style={{
                    display: 'flex', gap: '9px', justifyContent: 'flex-end', flexWrap: 'wrap',
                    padding: '12px 20px', background: '#faf9f6', borderTop: '1px solid #eeebe5',
                    // Never compressed by a long message above it, and never
                    // scrolled away with it.
                    flex: '0 0 auto',
                }}>
                    {/* Both keys carry qm-confirm-key: 44px tall on a phone (components.css). */}
                    {cancelText && (
                        <button
                            onClick={() => { onCancel?.(); onClose(); }}
                            data-qm-default-focus={defaultFocus === 'cancel' ? '' : undefined}
                            className="qm-inline-action-btn qm-confirm-key"
                            style={{ ...KEY_BASE, background: '#ffffff', border: '1px solid #d6d2cb', color: '#374151', boxShadow: '0 1px 2px rgba(26,26,26,0.10)' }}
                            onMouseEnter={lift} onMouseLeave={drop}
                        >
                            {cancelText}
                        </button>
                    )}
                    <button
                        onClick={() => { onConfirm(); onClose(); }}
                        data-qm-default-focus={defaultFocus === 'confirm' ? '' : undefined}
                        className="qm-inline-action-btn qm-confirm-key"
                        style={{ ...KEY_BASE, background: t.fill, border: `1px solid ${t.edge}`, color: '#ffffff', boxShadow: '0 1px 2px rgba(26,26,26,0.10)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = t.hover; lift(e); }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = t.fill; drop(e); }}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
